import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SOCIAL } from "@/config/links";
import { trackCta, capture, getPageSlug, captureWhatsAppClickEvent } from "@/lib/analytics";

/**
 * Persistent WhatsApp CTA — bottom-LEFT (opposite side to the Book a Consultation
 * pill on the right, so the two never collide on desktop). On mobile it sits above
 * the sticky book bar with safe-area padding.
 */

const STORAGE_KEY = "iag_stickywhatsapp_dismissed";
const DEFAULT_MESSAGE =
  "Hi Christos, I found you through interactglobally.com and I'd love to talk about";

export const StickyWhatsApp = () => {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(sessionStorage.getItem(STORAGE_KEY) === "1");
    const onScroll = () => setVisible(window.scrollY > 480);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dismiss = () => {
    sessionStorage.setItem(STORAGE_KEY, "1");
    setDismissed(true);
  };

  if (dismissed) return null;

  const href = `${SOCIAL.whatsapp}?text=${encodeURIComponent(DEFAULT_MESSAGE)}`;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          className="fixed z-[70] left-4 bottom-24 md:left-6 md:bottom-6"
          style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
          data-testid="sticky-whatsapp"
        >
          <div className="relative flex items-center">
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Message us on WhatsApp"
              data-testid="sticky-whatsapp-link"
              onClick={() => {
                // Opens in a new tab — the click itself, not a page
                // navigation, is the only signal we ever get for this
                // button, so it fires both the standard cta_click (for
                // page_slug/location consistency with every other CTA)
                // and a dedicated whatsapp_click event (easy to filter
                // on its own, answering "how many leads reach out via
                // WhatsApp, from which page").
                trackCta({ ctaId: "sticky-whatsapp-link", location: "sticky_whatsapp", href: SOCIAL.whatsapp });
                capture("whatsapp_click", { cta_location: "sticky_whatsapp", page_path: window.location.pathname, page_slug: getPageSlug() });
                // ChatGPT Ads custom conversion events, pass 2 (2026-02)
                // — business rule confirmed: the click itself IS the
                // conversion (no inbound-message webhook exists).
                // Classified by the CURRENT page only; fires nothing on
                // an ambiguous page, never both journeys for one click.
                captureWhatsAppClickEvent();
              }}
              className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-[0_10px_28px_-6px_rgba(37,211,102,0.55)] hover:scale-105 transition-transform duration-300"
            >
              <svg viewBox="0 0 32 32" width="28" height="28" fill="currentColor" aria-hidden="true">
                <path d="M16 3C9 3 3.4 8.7 3.4 15.6c0 2.4.7 4.7 1.9 6.7L3 29l6.9-2.1a13 13 0 0 0 6.1 1.5c7 0 12.6-5.7 12.6-12.6C28.6 8.7 23 3 16 3Zm0 22.7c-2 0-4-.6-5.7-1.7l-.4-.2-4.1 1.3 1.3-4-.3-.4A10.5 10.5 0 0 1 5.6 15.6c0-5.7 4.7-10.4 10.4-10.4s10.4 4.7 10.4 10.4-4.7 10.4-10.4 10.4Zm5.7-7.8c-.3-.2-1.8-.9-2.1-1s-.5-.2-.7.2c-.2.3-.8 1-1 1.2s-.3.2-.6 0c-.3-.2-1.3-.5-2.5-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.4.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5 0-.2-.7-1.7-1-2.3-.3-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1 2.1 3.2 5 4.5c.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.1-.3-.2-.6-.4Z" />
              </svg>
            </a>
            <button
              onClick={dismiss}
              aria-label="Dismiss WhatsApp"
              data-testid="sticky-whatsapp-dismiss"
              className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-ink text-paper text-xs flex items-center justify-center shadow"
            >
              &times;
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
